import React, { useState, useEffect } from 'react';
import { ArrowLeft, Clock, Calendar, ThumbsUp, ThumbsDown, ChevronRight, Tag } from 'react-feather';
import * as Icons from 'react-feather';
import AgentLayout from '../AgentLayout';
import { getCategoryBySlug, getArticle } from './supportData';
import '../AgentDashboard.css';

const SupportArticle = ({
  categorySlug,
  articleSlug,
  agentInfo,
  onDashboard,
  onMyListings,
  onAddProperty,
  onLogout,
  onInvitations,
  onShowings,
  onConversations,
  onAvailableLeads,
  onTeamRoster,
  onSupportHub,
  onNavigateToHub,
  onNavigateToCategory,
  onNavigateToArticle
}) => {
  const [category, setCategory] = useState(null);
  const [article, setArticle] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'helpful' | 'not-helpful' | null
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const cat = getCategoryBySlug(categorySlug);
    setCategory(cat);

    if (cat) {
      const art = getArticle(categorySlug, articleSlug);
      setArticle(art);

      // Get related articles (other articles in same category, excluding current)
      if (art) {
        const related = cat.articles
          .filter(a => a.slug !== articleSlug)
          .slice(0, 3);
        setRelatedArticles(related);
      }
    }
  }, [categorySlug, articleSlug]);

  const handleBackClick = () => {
    if (onNavigateToCategory) {
      onNavigateToCategory(categorySlug);
    }
  };

  const handleHubClick = () => {
    if (onNavigateToHub) {
      onNavigateToHub();
    }
  };

  const handleFeedback = (type) => {
    setFeedback(type);
    // Here you could send feedback to your backend
    console.log(`Article feedback: ${type} for ${articleSlug}`);
  };

  const handleRelatedClick = (relatedSlug) => {
    if (onNavigateToArticle) {
      onNavigateToArticle(categorySlug, relatedSlug);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent size={18} /> : null;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Simple markdown-ish renderer
  const renderContent = (content) => {
    if (!content) return null;

    const lines = content.trim().split('\n');
    const elements = [];
    let currentList = [];
    let listType = null;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${elements.length}`} style={styles.list}>
            {currentList.map((item, i) => (
              <li key={i} style={styles.listItem}>{item}</li>
            ))}
          </ul>
        );
        currentList = [];
        listType = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Headers
      if (trimmed.startsWith('# ')) {
        flushList();
        elements.push(
          <h1 key={index} style={styles.h1}>{trimmed.slice(2)}</h1>
        );
      } else if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={index} style={styles.h2}>{trimmed.slice(3)}</h2>
        );
      } else if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={index} style={styles.h3}>{trimmed.slice(4)}</h3>
        );
      }
      // List items
      else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        currentList.push(trimmed.slice(2));
      }
      // Numbered list
      else if (/^\d+\.\s/.test(trimmed)) {
        currentList.push(trimmed.replace(/^\d+\.\s/, ''));
      }
      // Empty line
      else if (trimmed === '') {
        flushList();
      }
      // Regular paragraph
      else if (trimmed.length > 0) {
        flushList();
        // Handle bold text
        const withBold = trimmed.split(/\*\*(.*?)\*\*/g).map((part, i) => {
          if (i % 2 === 1) {
            return <strong key={i} style={styles.bold}>{part}</strong>;
          }
          return part;
        });
        elements.push(
          <p key={index} style={styles.paragraph}>{withBold}</p>
        );
      }
    });

    flushList();
    return elements;
  };

  if (!category || !article) {
    return (
      <AgentLayout
        activeTab="support"
        onDashboard={onDashboard}
        onMyListings={onMyListings}
        onAddProperty={onAddProperty}
        onInvitations={onInvitations}
        onShowings={onShowings}
        onConversations={onConversations}
        onAvailableLeads={onAvailableLeads}
        onTeamRoster={onTeamRoster}
        onSupportHub={onSupportHub}
        onLogout={onLogout}
      >
        <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
          Article not found
        </div>
      </AgentLayout>
    );
  }

  const styles = {
    container: {
      padding: '40px',
      maxWidth: '900px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 10
    },
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '32px',
      fontSize: '0.85rem',
      flexWrap: 'wrap'
    },
    breadcrumbLink: {
      color: 'rgba(255, 255, 255, 0.5)',
      cursor: 'pointer',
      transition: 'color 0.15s ease',
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit'
    },
    breadcrumbSeparator: {
      color: 'rgba(255, 255, 255, 0.25)'
    },
    breadcrumbCurrent: {
      color: 'rgba(255, 255, 255, 0.8)',
      maxWidth: '300px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '0.9rem',
      cursor: 'pointer',
      marginBottom: '24px',
      padding: '8px 12px',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      border: 'none',
      background: 'transparent'
    },
    header: {
      marginBottom: '40px'
    },
    categoryBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      borderRadius: '8px',
      fontSize: '0.8rem',
      fontWeight: '500',
      marginBottom: '16px'
    },
    title: {
      fontSize: '2.25rem',
      fontWeight: '700',
      color: '#ffffff',
      marginBottom: '16px',
      lineHeight: '1.3',
      letterSpacing: '-0.5px'
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      color: 'rgba(255, 255, 255, 0.5)',
      fontSize: '0.85rem',
      flexWrap: 'wrap'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    articleContent: {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      borderRadius: '16px',
      padding: '40px',
      marginBottom: '32px'
    },
    // Content styles
    h1: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: '#ffffff',
      marginTop: '0',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    },
    h2: {
      fontSize: '1.35rem',
      fontWeight: '600',
      color: '#ffffff',
      marginTop: '32px',
      marginBottom: '16px'
    },
    h3: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: 'rgba(255, 255, 255, 0.9)',
      marginTop: '24px',
      marginBottom: '12px'
    },
    paragraph: {
      fontSize: '1rem',
      lineHeight: '1.75',
      color: 'rgba(255, 255, 255, 0.75)',
      marginBottom: '16px'
    },
    bold: {
      color: '#ffffff',
      fontWeight: '600'
    },
    list: {
      margin: '16px 0',
      paddingLeft: '24px'
    },
    listItem: {
      fontSize: '1rem',
      lineHeight: '1.75',
      color: 'rgba(255, 255, 255, 0.75)',
      marginBottom: '8px'
    },
    // Feedback section
    feedbackSection: {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      borderRadius: '12px',
      padding: '24px',
      textAlign: 'center',
      marginBottom: '32px'
    },
    feedbackTitle: {
      fontSize: '1rem',
      fontWeight: '500',
      color: '#ffffff',
      marginBottom: '16px'
    },
    feedbackButtons: {
      display: 'flex',
      justifyContent: 'center',
      gap: '12px'
    },
    feedbackButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: 'rgba(255, 255, 255, 0.7)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '0.9rem'
    },
    feedbackButtonActive: {
      backgroundColor: 'rgba(99, 102, 241, 0.2)',
      borderColor: 'rgba(99, 102, 241, 0.5)',
      color: '#ffffff'
    },
    feedbackThanks: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '0.9rem'
    },
    // Tags
    tagsSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
      marginBottom: '32px'
    },
    tagsLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      color: 'rgba(255, 255, 255, 0.4)',
      fontSize: '0.85rem'
    },
    tag: {
      padding: '5px 12px',
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderRadius: '6px',
      fontSize: '0.8rem',
      color: 'rgba(255, 255, 255, 0.6)'
    },
    // Related articles
    relatedSection: {
      marginTop: '48px'
    },
    relatedTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '16px'
    },
    relatedGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '12px'
    },
    relatedCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      borderRadius: '10px',
      padding: '16px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    relatedCardTitle: {
      fontSize: '0.9rem',
      fontWeight: '500',
      color: '#ffffff',
      marginBottom: '6px'
    },
    relatedCardMeta: {
      fontSize: '0.8rem',
      color: 'rgba(255, 255, 255, 0.4)'
    }
  };

  const articleContent = (
    <div style={styles.container}>
      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <button
          style={styles.breadcrumbLink}
          onClick={handleHubClick}
          onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
        >
          Help Center
        </button>
        <ChevronRight size={14} style={styles.breadcrumbSeparator} />
        <button
          style={styles.breadcrumbLink}
          onClick={handleBackClick}
          onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
        >
          {category.title}
        </button>
        <ChevronRight size={14} style={styles.breadcrumbSeparator} />
        <span style={styles.breadcrumbCurrent}>{article.title}</span>
      </div>

      {/* Back Button */}
      <button
        style={styles.backButton}
        onClick={handleBackClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
          e.currentTarget.style.color = '#ffffff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
        }}
      >
        <ArrowLeft size={18} />
        Back to {category.title}
      </button>

      {/* Header */}
      <div style={styles.header}>
        <div
          style={{
            ...styles.categoryBadge,
            backgroundColor: `${category.color}15`,
            color: category.color
          }}
        >
          {getIcon(category.icon)}
          {category.title}
        </div>
        <h1 style={styles.title}>{article.title}</h1>
        <div style={styles.meta}>
          <div style={styles.metaItem}>
            <Clock size={16} />
            {article.readTime} min read
          </div>
          <div style={styles.metaItem}>
            <Calendar size={16} />
            Updated {formatDate(article.updatedAt)}
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div style={styles.articleContent}>
        {renderContent(article.content)}
      </div>

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <div style={styles.tagsSection}>
          <div style={styles.tagsLabel}>
            <Tag size={14} />
            Tags:
          </div>
          {article.tags.map((tag) => (
            <span key={tag} style={styles.tag}>{tag}</span>
          ))}
        </div>
      )}

      {/* Feedback */}
      <div style={styles.feedbackSection}>
        {!feedback ? (
          <>
            <div style={styles.feedbackTitle}>Was this article helpful?</div>
            <div style={styles.feedbackButtons}>
              <button
                style={styles.feedbackButton}
                onClick={() => handleFeedback('helpful')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                }}
              >
                <ThumbsUp size={18} />
                Yes, helpful
              </button>
              <button
                style={styles.feedbackButton}
                onClick={() => handleFeedback('not-helpful')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                }}
              >
                <ThumbsDown size={18} />
                Not really
              </button>
            </div>
          </>
        ) : (
          <div style={styles.feedbackThanks}>
            Thanks for your feedback! {feedback === 'helpful' ? '🎉' : 'We\'ll work on improving this.'}
          </div>
        )}
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div style={styles.relatedSection}>
          <div style={styles.relatedTitle}>Related Articles</div>
          <div style={styles.relatedGrid}>
            {relatedArticles.map((related) => (
              <div
                key={related.slug}
                style={styles.relatedCard}
                onClick={() => handleRelatedClick(related.slug)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.03)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.06)';
                }}
              >
                <div style={styles.relatedCardTitle}>{related.title}</div>
                <div style={styles.relatedCardMeta}>{related.readTime} min read</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <AgentLayout
      activeTab="support"
      onDashboard={onDashboard}
      onMyListings={onMyListings}
      onAddProperty={onAddProperty}
      onInvitations={onInvitations}
      onShowings={onShowings}
      onConversations={onConversations}
      onAvailableLeads={onAvailableLeads}
      onTeamRoster={onTeamRoster}
      onSupportHub={onSupportHub}
      onLogout={onLogout}
    >
      {articleContent}
    </AgentLayout>
  );
};

export default SupportArticle;